import { takeLatest, put, call } from 'redux-saga/effects';
import { AxiosError } from 'axios';

import i18n from '@components/i18n';
import { getResponseError } from '@utils/error';
import { history } from '@utils/history';
import { CamDxApi } from '@apis/CamDxApi';
import { SagaPayload } from '@utils/typescript';
import { authAccessTokenSet } from '@components/Auth';
import { AFTER_SIGN_IN_PATH, SIGNIN_PATH } from '@config';
import { toastShow } from '@components/ToastProvider';
import { OtpApi } from '@apis/OtpApi';
import { User } from '@models/User';
import { AuthMemberIdApi } from '@apis/AuthMemberIdApi';
import { AuthOtpCodeApi } from '@apis/AuthOtpCodeApi';
import { AuthPasswordApi } from '@apis/AuthPasswordApi';

import { routeAuthOtpCodePage, routeAuthPasswordPage } from './route';
import {
  otpCodeSignIn,
  OtpCodeSignInPayload,
  otpCodeCheck,
  OtpCodeCheckPayload,
  otpCodeVerify,
  OtpCodeVerifyPayload,
  passwordSignIn,
  PasswordSignInPayload,
  passwordVerify,
  PasswordVerifyPayload,
  memberIdRegister,
  MemberIdRegisterPayload,
  camDxSignIn,
  passwordUpdate,
  PasswordUpdatePayload,
  otpUserFetch,
  OtpUserFetchPayload,
  stateUpdate,
  StateUpdatePayload,
} from './action';


function* handleSignIn(accessToken: string) {
  yield put(authAccessTokenSet(accessToken));
  const from = history.location.state?.from;
  const path = from ? `${from.pathname}${from.search}` : AFTER_SIGN_IN_PATH;
  history.navigate(path);
  yield put(toastShow({ status: 'success', body: i18n.t('toast.signedIn') }));
}

function* handleOtpCodeSignIn({ payload }: SagaPayload<OtpCodeSignInPayload>) {
  try {
    yield call(AuthOtpCodeApi.check, payload);
    history.navigate(routeAuthOtpCodePage(payload), { state: history.location.state });
  } catch (e) {
    const { error } = getResponseError(e as AxiosError);
    const state: StateUpdatePayload = { serverErrors: { mobileNumber: error } };
    yield put(stateUpdate(state));
  }
}

function* handleOtpCodeCheck({ payload }: SagaPayload<OtpCodeCheckPayload>) {
  try {
    yield call(AuthOtpCodeApi.check, payload);
  } catch (e) {
    const { error } = getResponseError(e as AxiosError);
    const state: StateUpdatePayload = { serverErrors: { otpCode: error } };
    yield put(stateUpdate(state));
  }
}

function* handleOtpCodeVerify({ payload }: SagaPayload<OtpCodeVerifyPayload>) {
  try {
    const { accessToken } = (yield call(AuthOtpCodeApi.verify, payload)) as { accessToken: string };
    yield handleSignIn(accessToken);
  } catch (e) {
    const { error } = getResponseError(e as AxiosError);
    const state: StateUpdatePayload = { serverErrors: { otpCode: error } };
    yield put(stateUpdate(state));
  }
}

function* handlePasswordSignIn({ payload }: SagaPayload<PasswordSignInPayload>) {
  try {
    yield call(AuthPasswordApi.check, payload);
    history.navigate(routeAuthPasswordPage(payload), { state: history.location.state });
  } catch (e) {
    const { error } = getResponseError(e as AxiosError);
    const state: StateUpdatePayload = { serverErrors: { mobileNumber: error } };
    yield put(stateUpdate(state));
  }
}

function* handlePasswordVerify({ payload }: SagaPayload<PasswordVerifyPayload>) {
  try {
    const { accessToken } = (yield call(AuthPasswordApi.verify, payload)) as { accessToken: string };
    yield handleSignIn(accessToken);
  } catch (e) {
    const { error } = getResponseError(e as AxiosError);
    const state: StateUpdatePayload = { serverErrors: { password: error } };
    yield put(stateUpdate(state));
  }
}

function* handleMemberIdRegister({ payload }: SagaPayload<MemberIdRegisterPayload>) {
  try {
    const { accessToken } = (yield call(AuthMemberIdApi.create, payload)) as { accessToken: string };
    yield handleSignIn(accessToken);
  } catch (e) {
    const { error } = getResponseError(e as AxiosError);
    const state: StateUpdatePayload = { error };
    yield put(stateUpdate(state));
  }
}

function* handleCamDxSignIn() {
  try {
    const signInUrl = (yield call(CamDxApi.getSignInUrl)) as string;
    window.location.href = signInUrl;
  } catch (e) {
    const { error } = getResponseError(e as AxiosError);
    const state: StateUpdatePayload = { error };
    yield put(stateUpdate(state));
  }
}

function* handlePasswordUpdate({ payload }: SagaPayload<PasswordUpdatePayload>) {
  const state: StateUpdatePayload = { errors: [] };
  try {
    yield call(OtpApi.updatePassword, payload);
    yield put(stateUpdate(state));
    history.navigate(SIGNIN_PATH, { replace: true });
  } catch (error) {
    state.errors?.push((error as Error).message);
    yield put(stateUpdate(state));
  }
}

function* handleOtpUserFetch({ payload }: SagaPayload<OtpUserFetchPayload>) {
  const state: StateUpdatePayload = { user: null };
  try {
    state.user = (yield call(OtpApi.get, payload)) as User;
    yield put(stateUpdate(state));
  } catch (error) {
    state.errors?.push((error as Error).message);
    yield put(stateUpdate(state));
  }
}

export default function* () {
  yield takeLatest(otpCodeSignIn, handleOtpCodeSignIn);
  yield takeLatest(otpCodeCheck, handleOtpCodeCheck);
  yield takeLatest(otpCodeVerify, handleOtpCodeVerify);
  yield takeLatest(passwordSignIn, handlePasswordSignIn);
  yield takeLatest(passwordVerify, handlePasswordVerify);
  yield takeLatest(memberIdRegister, handleMemberIdRegister);
  yield takeLatest(camDxSignIn, handleCamDxSignIn);
  yield takeLatest(passwordUpdate, handlePasswordUpdate);
  yield takeLatest(otpUserFetch, handleOtpUserFetch);
}
